import { inject, injectable } from 'inversify';
import type { IMeasurementViewModel, IObjectViewModel } from '../../interfaces';
import { SceneItemKind, Transparency } from '../../interfaces';
import type { IIndicationColor, IIndicationsViewModelFactory } from './interfaces';
import { BasicObjectViewModel } from '../../models/BasicObjectViewModel';
import { v4 as uuid } from 'uuid';
import { MeasurementViewModel } from '../../models/MeasurementViewModel';
import { defaultMeasurementModelParams } from '../../View.factory';
import { ImagesMode, SliceType } from '../images/interfaces';
import { AnnotationViewModel } from './annotation/Annotation.viewModel';
import { LightenDarkenColor } from '@/engine/utils/colorUtils';
import { TYPES } from '@/engine/ioc';
import { UIConfigResolver } from '../ui';

@injectable()
export class IndicationsViewModelFactory implements IIndicationsViewModelFactory {
  private _colorMapping: Partial<Record<SceneItemKind, IIndicationColor>> = {
    [SceneItemKind.IndicatedSpline]: {
      color: '#93ff3c',
      highlightColor: defaultMeasurementModelParams.highlightColor,
      selectionColor: defaultMeasurementModelParams.selectionColor
    },
    [SceneItemKind.DistanceMeasurement]: {
      color: '#00A3FF',
      highlightColor: defaultMeasurementModelParams.highlightColor,
      selectionColor: defaultMeasurementModelParams.selectionColor
    },
    [SceneItemKind.LabeledDistanceMeasurement]: {
      color: '#00A3FF',
      highlightColor: defaultMeasurementModelParams.highlightColor,
      selectionColor: defaultMeasurementModelParams.selectionColor
    }
  };

  private readonly _uiConfigResolver: UIConfigResolver;

  private _areIndicationsImplicitlyEditable?: boolean;

  public constructor(@inject(TYPES.UIConfigResolver) uiConfigResolver: UIConfigResolver) {
    this._uiConfigResolver = uiConfigResolver;
  }

  private get _isIndicationBeingEdited(): boolean {
    if (this._areIndicationsImplicitlyEditable === undefined) {
      this._areIndicationsImplicitlyEditable = this._uiConfigResolver.getValueByDescriptor(
        'areIndicationsImplicitlyEditable'
      );
    }
    return this._areIndicationsImplicitlyEditable;
  }

  private _getColorConfig(kind: SceneItemKind): IIndicationColor {
    return this._colorMapping[kind] || { color: '#FF0000', highlightColor: '#FFC949', selectionColor: '#E5A100' };
  }

  public createViewModel(
    kind: SceneItemKind,
    phaseId?: string,
    descriptor?: string,
    attachments?: Array<string>,
    label?: string,
    syncSliceDescriptor?: SliceType,
    isVisible?: boolean,
    isClippable?: boolean,
    pointColor?: string
  ): IObjectViewModel {
    const { color, highlightColor, selectionColor } = this._getColorConfig(kind);
    const definedSelectionColor = pointColor ? LightenDarkenColor(pointColor, 10) : selectionColor;
    const definedHighlightingColor = pointColor ? LightenDarkenColor(pointColor, 10) : highlightColor;

    switch (kind) {
      case SceneItemKind.CuspPoint:
      case SceneItemKind.VariableDistancePoint:
      case SceneItemKind.CommissurePoint:
      case SceneItemKind.IndicatedDistance:
      case SceneItemKind.IndicatedAngle:
      case SceneItemKind.IndicatedSpline:
      case SceneItemKind.AreaIndication: {
        return new BasicObjectViewModel({
          color: pointColor ?? color,
          selectionColor: definedSelectionColor,
          highlightColor: definedHighlightingColor,
          descriptor: descriptor ?? uuid(),
          details: undefined,
          isVisible: isVisible ?? true,
          label: label ?? '',
          transparency: Transparency.Opaque,
          isHighlighted: false,
          isSelected: false,
          isPickable: true,
          isTunable: true,
          isBeingEdited: this._isIndicationBeingEdited,
          isVertexColoring: false,
          isContourVisible: false,
          attachments: attachments ?? [],
          kind: kind,
          imported: false,
          isAvailable: true,
          isClippable: isClippable ?? true,
          phaseId,
          syncSliceDescriptor
        });
      }

      case SceneItemKind.IndicatedAnnotation: {
        return new AnnotationViewModel({
          color,
          highlightColor,
          selectionColor,
          descriptor,
          isVisible,
          phaseId,
          label: label ?? '',
          isPickable: true,
          isTunable: true,
          isBeingEdited: this._isIndicationBeingEdited
        });
      }
      default: {
        throw new Error('Incorrect kind');
      }
    }
  }

  public createMeasurementViewModel(
    kind: SceneItemKind,
    phaseId?: string,
    descriptor?: string,
    attachments?: Array<string>,
    label?: string,
    zoomFactor?: number,
    isVisible?: boolean,
    details?: unknown
  ): IMeasurementViewModel {
    const { color, highlightColor, selectionColor } = this._getColorConfig(kind);
    switch (kind) {
      case SceneItemKind.DistanceMeasurement:
      case SceneItemKind.LabeledDistanceMeasurement:
      case SceneItemKind.IndicatedSplineMeasurement:
      case SceneItemKind.IndicatedAreaMeasurement: {
        return new MeasurementViewModel({
          descriptor: descriptor ?? uuid(),
          attachments: attachments ?? [],
          label: label ?? '',
          phaseId: phaseId,
          details,
          syncSliceDescriptor: SliceType.NONE,
          color,
          highlightColor,
          selectionColor,
          isVisible: isVisible ?? true,
          isHighlighted: false,
          isSelected: false,
          transparency: Transparency.Opaque,
          isPickable: true,
          isTunable: true,
          isBeingEdited: false,
          isContourVisible: true,
          kind: kind,
          zoomFactor: zoomFactor ?? defaultMeasurementModelParams.zoomFactor,
          imagesMode: ImagesMode.MPR,
          isAvailable: true,
          readyForSliding: false,
          isClippable: true,
          imported: false
        });
      }
      default: {
        throw new Error('Incorrect kind');
      }
    }
  }
}
